import React, { useState } from 'react';
import { recordConsumableUsage } from '../../utils/itemOperations';
import { useAuth } from '../../context/AuthContext';

const ConsumableUsageForm = ({ item, onUsageRecorded }) => {
  const { currentUser, userDetails } = useAuth();
  const [amountUsed, setAmountUsed] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!amountUsed || parseFloat(amountUsed) <= 0) {
      setError('Please enter a valid amount used');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const usageData = {
        userId: currentUser?.uid || null,
        userName: userDetails?.name || currentUser?.displayName || null,
        pid: userDetails?.pid || null,
        notes: notes.trim()
      };

      await recordConsumableUsage(item.id, parseFloat(amountUsed), usageData);

      setSuccess(`Successfully recorded usage of ${amountUsed} ${item.unit}`);
      setAmountUsed('');
      setNotes('');

      onUsageRecorded && onUsageRecorded();
    } catch (error) {
      console.error('Error recording usage:', error);
      setError(error.message || 'Failed to record usage');
    } finally {
      setLoading(false);
    }
  };

  const currentStock = parseFloat(item.currentStock) || 0;
  const lowStockAlert = parseFloat(item.lowStockAlert) || 0;
  const isLowStock = lowStockAlert > 0 && currentStock <= lowStockAlert;
  const stockPercentage = item.initialStock ? (currentStock / item.initialStock) * 100 : 0;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Record Usage</h3>
        <div className="text-right">
          <div className="text-sm text-gray-500">Current Stock</div>
          <div className={`text-lg font-bold ${isLowStock ? 'text-red-600' : 'text-green-600'}`}>
            {currentStock} {item.unit}
            {isLowStock && <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">Low Stock</span>}
          </div>
          <div className="text-xs text-gray-400">
            {stockPercentage.toFixed(1)}% of initial stock
          </div>
        </div>
      </div>

      {/* Stock Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>Stock Level</span>
          <span>{currentStock} / {item.initialStock} {item.unit}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              stockPercentage > 50 ? 'bg-green-500' :
              stockPercentage > 20 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
            style={{ width: `${Math.max(stockPercentage, 2)}%` }}
          ></div>
        </div>
        {lowStockAlert > 0 && (
          <div className="text-xs text-gray-500 mt-1">
            Low stock alert at {lowStockAlert} {item.unit}
          </div>
        )}
      </div>

      {currentStock <= 0 ? (
        <div className="text-center py-8">
          <div className="text-red-600 text-lg font-medium">Out of Stock</div>
          <div className="text-gray-500">This consumable needs to be restocked before use.</div>
        </div>
      ) : (
        <>
          {error && (
            <div className="mb-4 p-3 bg-red-100 border-l-4 border-red-500 text-red-700">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-100 border-l-4 border-green-500 text-green-700">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount Used *
              </label>
              <div className="flex">
                <input
                  type="number"
                  value={amountUsed}
                  onChange={(e) => setAmountUsed(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-black"
                  placeholder="0"
                  min="0"
                  step="0.01"
                  max={currentStock}
                  required
                />
                <span className="px-3 py-2 bg-gray-50 border border-l-0 border-gray-300 rounded-r-md text-gray-700">
                  {item.unit}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Maximum: {currentStock} {item.unit}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-black"
                rows={3}
                placeholder="What are you using this for? Any observations about the consumable?"
              />
            </div>

            <button
              type="submit"
              disabled={loading || currentStock <= 0}
              className="w-full py-2 px-4 bg-blue-500 text-white font-medium rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Recording...' : 'Record Usage'}
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default ConsumableUsageForm;
